import { useCallback, useEffect, useState } from 'react';
import { AdminFormPageShimmer } from '../../common/Shimmer.jsx';
import { hpApi } from './hpApi.js';
import { HpIcon, HpField, useHpToast, useUnsavedGuard } from './HpUi.jsx';

const FIXED_KEYS = {
  companyName: '会社名',
  address: '所在地（フッターにも表示）',
  representative: '代表者',
  established: '設立',
  business: '事業内容',
};

function newKey() {
  return `row${Date.now().toString(36)}`;
}

export default function HpCompanyPanel({ canWrite }) {
  const toast = useHpToast();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  useUnsavedGuard(dirty);

  const load = useCallback(async () => {
    try {
      const res = await hpApi.getCompany();
      setData(res.company);
      setDirty(false);
    } catch (err) {
      toast.error(err.message || '会社情報の取得に失敗しました');
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <AdminFormPageShimmer fields={6} withHeader={false} />;

  const set = (patch) => { setData((d) => ({ ...d, ...patch })); setDirty(true); };
  const setRow = (i, patch) => set({ profileRows: data.profileRows.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const moveRow = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= data.profileRows.length) return;
    const rows = [...data.profileRows];
    [rows[i], rows[j]] = [rows[j], rows[i]];
    set({ profileRows: rows });
  };

  const save = async () => {
    if (data.profileRows.some((r) => !r.label.trim() || !r.value.trim())) {
      toast.error('会社概要の項目名と内容をすべて入力してください');
      return;
    }
    setSaving(true);
    try {
      await hpApi.saveCompany(data);
      toast.success('会社情報を保存しました。HP にはおよそ1分以内に反映されます');
      await load();
    } catch (err) {
      toast.error(err.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="hp-panel">
      <section className="hp-card">
        <div className="hp-card__head">
          <h3 className="hp-card__title">会社概要</h3>
          <span className="hp-card__sub">会社概要ページの表に、この順で表示されます</span>
        </div>
        <div className="hp-rows">
          <div className="hp-rows__head">
            <span>項目名</span><span>内容</span><span>トップに表示</span><span />
          </div>
          {data.profileRows.map((row, i) => (
            <div className="hp-rows__row" key={row.key}>
              <div>
                <input className="hp-input" value={row.label} disabled={!canWrite} onChange={(e) => setRow(i, { label: e.target.value })} aria-label="項目名" />
                {FIXED_KEYS[row.key] && <span className="hp-rows__tag">{FIXED_KEYS[row.key]}</span>}
              </div>
              <textarea className="hp-textarea" rows={row.value.length > 40 ? 3 : 1} value={row.value} disabled={!canWrite} onChange={(e) => setRow(i, { value: e.target.value })} aria-label="内容" />
              <label className="hp-check">
                <input type="checkbox" checked={row.showOnTop} disabled={!canWrite} onChange={(e) => setRow(i, { showOnTop: e.target.checked })} />
                <span>表示</span>
              </label>
              {canWrite ? (
                <div className="hp-rows__tools">
                  <button type="button" className="hp-icon-btn" onClick={() => moveRow(i, -1)} disabled={i === 0} aria-label="上へ"><HpIcon.Up /></button>
                  <button type="button" className="hp-icon-btn" onClick={() => moveRow(i, 1)} disabled={i === data.profileRows.length - 1} aria-label="下へ"><HpIcon.Down /></button>
                  <button
                    type="button"
                    className="hp-icon-btn hp-icon-btn--danger"
                    onClick={() => set({ profileRows: data.profileRows.filter((_, j) => j !== i) })}
                    disabled={Boolean(FIXED_KEYS[row.key])}
                    title={FIXED_KEYS[row.key] ? 'この項目は他の場所でも使うため削除できません' : undefined}
                    aria-label="行を削除"
                  >
                    <HpIcon.Trash />
                  </button>
                </div>
              ) : <span />}
            </div>
          ))}
        </div>
        {canWrite && (
          <button type="button" className="hp-btn hp-btn--ghost hp-btn--sm" onClick={() => set({ profileRows: [...data.profileRows, { key: newKey(), label: '', value: '', showOnTop: false }] })}>
            <HpIcon.Plus size={12} />行を追加
          </button>
        )}
      </section>

      <section className="hp-card">
        <h3 className="hp-card__title">連絡先・検索エンジン向け情報</h3>
        <div className="hp-row">
          <HpField label="連絡先メールアドレス" required hint="フッター・お問い合わせページ・検索エンジン向け情報に使われます">
            <input className="hp-input" type="email" value={data.contactEmail} disabled={!canWrite} onChange={(e) => set({ contactEmail: e.target.value })} />
          </HpField>
          <HpField label="設立日" required hint="検索エンジン向け情報（構造化データ）に使われます">
            <input className="hp-input" type="date" value={data.foundingDate} disabled={!canWrite} onChange={(e) => set({ foundingDate: e.target.value })} />
          </HpField>
        </div>
        <p className="hp-field__hint hp-field__hint--block">検索エンジン向けの住所（構造化データ）。画面には表示されません。所在地を変更したときは合わせて更新してください。</p>
        <div className="hp-row hp-row--3">
          <HpField label="都道府県" required>
            <input className="hp-input" value={data.ldAddress.region} disabled={!canWrite} onChange={(e) => set({ ldAddress: { ...data.ldAddress, region: e.target.value } })} />
          </HpField>
          <HpField label="市区町村" required>
            <input className="hp-input" value={data.ldAddress.locality} disabled={!canWrite} onChange={(e) => set({ ldAddress: { ...data.ldAddress, locality: e.target.value } })} />
          </HpField>
          <HpField label="番地・建物" required>
            <input className="hp-input" value={data.ldAddress.street} disabled={!canWrite} onChange={(e) => set({ ldAddress: { ...data.ldAddress, street: e.target.value } })} />
          </HpField>
        </div>
      </section>

      {canWrite && (
        <div className="hp-savebar">
          {dirty && <span className="hp-unsaved">未保存の変更があります</span>}
          <button type="button" className="hp-btn hp-btn--ghost" onClick={load} disabled={!dirty || saving}>変更を取り消す</button>
          <button type="button" className="hp-btn hp-btn--primary" onClick={save} disabled={!dirty || saving}>{saving ? '保存中…' : '会社情報を保存'}</button>
        </div>
      )}
    </div>
  );
}
